import React, { useState } from 'react'
import { Table, Button, Input, Select, Space, Tag, Avatar } from 'antd'
import { PlusOutlined, EyeOutlined, EditOutlined, UserOutlined, SearchOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import type { ColumnsType } from 'antd/es/table'
import './PatientManagement.css'

const { Option } = Select

interface Patient {
  id: string
  name: string
  gender: string
  age: number
  chiefComplaint: string
  status: string
}

const PatientManagement: React.FC = () => {
  const navigate = useNavigate()
  const [searchText, setSearchText] = useState('')

  const columns: ColumnsType<Patient> = [
    {
      title: '头像',
      dataIndex: 'avatar',
      key: 'avatar',
      width: 80,
      render: () => <Avatar icon={<UserOutlined />} />,
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '性别',
      dataIndex: 'gender',
      key: 'gender',
      width: 80,
    },
    {
      title: '年龄',
      dataIndex: 'age',
      key: 'age',
      width: 80,
    },
    {
      title: '主诉',
      dataIndex: 'chiefComplaint',
      key: 'chiefComplaint',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const statusMap: Record<string, { color: string; text: string }> = {
          waiting: { color: 'default', text: '待诊' },
          diagnosing: { color: 'processing', text: '诊疗中' },
          completed: { color: 'success', text: '已完成' },
        }
        const statusInfo = statusMap[status] || { color: 'default', text: status }
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/diagnosis/workspace/${record.id}`)}
          >
            诊疗
          </Button>
          <Button type="link" icon={<EditOutlined />}>
            编辑
          </Button>
        </Space>
      ),
    },
  ]

  const data: Patient[] = [
    {
      id: '1',
      name: '张三',
      gender: '男',
      age: 45,
      chiefComplaint: '胸闷气短，心悸',
      status: 'waiting',
    },
    {
      id: '2',
      name: '李四',
      gender: '女',
      age: 32,
      chiefComplaint: '头痛，失眠',
      status: 'diagnosing',
    },
  ]

  return (
    <div className="patient-management-page">
      <div className="page-header">
        <h1 className="page-title">患者管理</h1>
        <Button type="primary" icon={<PlusOutlined />}>
          新建患者
        </Button>
      </div>

      <div className="page-filters">
        <Space>
          <Space.Compact style={{ width: 300 }}>
            <Input
              placeholder="搜索患者姓名..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
            <Button icon={<SearchOutlined />} type="primary" />
          </Space.Compact>
          <Select style={{ width: 120 }} defaultValue="all">
            <Option value="all">全部状态</Option>
            <Option value="waiting">待诊</Option>
            <Option value="diagnosing">诊疗中</Option>
            <Option value="completed">已完成</Option>
          </Select>
        </Space>
      </div>

      <div className="patient-table">
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          pagination={{
            total: data.length,
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 位患者`,
          }}
        />
      </div>
    </div>
  )
}

export default PatientManagement

